import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import { Badge, Banner, Card, DataTable, Page, Text, Toast } from '@shopify/polaris';
import type { BadgeProps } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '../components/ScrollArea';
import { apiClient, ApiError } from '../lib/api-client';
import { reconnectShopify } from '../lib/auth';
import { useApi } from '../lib/use-api';
import type { CouponStatusId, CouponSyncHealth, CouponSyncStatus, CouponView } from '../lib/types';

const STATUS_TONE: Partial<Record<CouponStatusId, BadgeProps['tone']>> = {
  active: 'success',
  redeemed: 'info',
  expired: 'warning',
  void: 'critical',
};

const SYNC_TONE: Record<CouponSyncStatus, BadgeProps['tone']> = {
  synced: 'success',
  pending: 'attention',
  failed: 'critical',
};
const SYNC_LABEL: Record<CouponSyncStatus, string> = {
  synced: 'Redeemable',
  pending: 'Syncing…',
  failed: 'Not redeemable',
};

function discountText(c: CouponView): string {
  if (c.discountType === 'percentage') return `${c.discountValue}% off`;
  if (c.discountType === 'fixed') return `$${c.discountValue} off`;
  return 'Free shipping';
}

function checkoutCell(c: CouponView): JSX.Element {
  if (!c.syncStatus) return <Text as="span" tone="subdued">—</Text>;
  const tone = SYNC_TONE[c.syncStatus];
  return <Badge {...(tone ? { tone } : {})}>{SYNC_LABEL[c.syncStatus]}</Badge>;
}

export function CouponsPage(): JSX.Element {
  const navigate = useNavigate();
  const coupons = useApi(() => apiClient.get<CouponView[]>('/coupons?limit=100'), []);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [syncHealth, setSyncHealth] = useState<CouponSyncHealth | null>(null);

  // Only coupons a shopper could still redeem count as problems. An expired or
  // already-redeemed coupon can never be synced (Shopify rejects a discount
  // that ends before it starts), so flagging it as a failure just shows the
  // merchant a permanent error they cannot act on.
  const failedCount = (coupons.data ?? []).filter(
    (c) => c.syncStatus === 'failed' && c.status === 'active',
  ).length;

  // Only worth checking once there's actually something failing — this hits
  // Shopify live (not our cached scope record), so don't call it needlessly.
  useEffect(() => {
    if (failedCount === 0) {
      setSyncHealth(null);
      return;
    }
    apiClient
      .get<CouponSyncHealth | null>('/coupons/sync-health')
      .then(setSyncHealth)
      .catch(() => setSyncHealth(null));
  }, [failedCount]);

  const syncOrders = async (): Promise<void> => {
    setSyncing(true);
    try {
      const result = await apiClient.post<{ scanned: number; recorded: number }>('/coupons/sync-orders');
      setToast(`Synced ${result.scanned} orders — ${result.recorded} redemption(s) recorded`);
      coupons.refetch();
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Could not sync orders');
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Re-authenticate with Shopify, then retry. A stale/rejected Shopify Admin
   * token can only be replaced by a fresh session-token exchange, which
   * otherwise happens just once per page load — so without this the merchant
   * has to close the whole tab (or reinstall) to recover.
   */
  const reconnectAndRetry = async (): Promise<void> => {
    setSyncing(true);
    try {
      const ok = await reconnectShopify();
      if (!ok) {
        setToast('Could not reconnect to Shopify — please reload the page and try again');
        return;
      }
      const result = await apiClient.post<{ scanned: number; recorded: number }>('/coupons/sync-orders');
      setToast(`Reconnected to Shopify — ${result.recorded} redemption(s) recorded`);
      coupons.refetch();
      apiClient
        .get<CouponSyncHealth | null>('/coupons/sync-health')
        .then(setSyncHealth)
        .catch(() => setSyncHealth(null));
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Could not reconnect to Shopify');
    } finally {
      setSyncing(false);
    }
  };

  const rows = (coupons.data ?? []).map((c) => {
    const tone = STATUS_TONE[c.status];
    return [
      <Text as="span" key={`${c.id}-code`} fontWeight="semibold">
        <code>{c.code}</code>
      </Text>,
      discountText(c),
      <Badge key={`${c.id}-status`} {...(tone ? { tone } : {})}>
        {c.status}
      </Badge>,
      checkoutCell(c),
      `${c.usedCount}${c.usageLimit ? ` / ${c.usageLimit}` : c.singleUse ? ' / 1' : ''}`,
      new Date(c.createdAt).toLocaleDateString(),
    ];
  });

  return (
    <Page
      title="Coupons"
      subtitle="Discount codes won by your customers"
      primaryAction={{
        content: 'Sync orders',
        onAction: () => void syncOrders(),
        loading: syncing,
        helpText: 'Pull recent orders and record which coupons were redeemed',
      }}
    >
      {coupons.loading ? <LoadingState label="Loading coupons" /> : null}
      {coupons.error ? <ErrorState message={coupons.error.message} onRetry={coupons.refetch} /> : null}

      {failedCount > 0 ? (
        <Banner
          tone="critical"
          title={`${failedCount} coupon${failedCount === 1 ? '' : 's'} not redeemable at checkout`}
          action={{
            content: 'Reconnect Shopify & retry',
            onAction: () => void reconnectAndRetry(),
            loading: syncing,
          }}
          secondaryAction={{ content: 'Retry sync only', onAction: () => void syncOrders() }}
        >
          {syncHealth?.tokenInvalid ? (
            <p>
              Confirmed: PlayForge&apos;s connection to your store is broken — Shopify is rejecting
              even basic requests, which means the stored access token itself is invalid or was
              revoked (not just missing a permission). Retrying sync won&apos;t fix this —{' '}
              <strong>fully uninstall and reinstall the app</strong> from Shopify to get a fresh
              token, then click Retry sync.
            </p>
          ) : syncHealth && syncHealth.missing.length > 0 ? (
            <p>
              Confirmed: your store hasn&apos;t granted{' '}
              {syncHealth.missing.map((s) => (
                <code key={s}>{s} </code>
              ))}
              to PlayForge. Retrying sync won&apos;t fix this — reinstall the app from Shopify to
              re-approve permissions, then click Retry sync.
            </p>
          ) : (
            <p>
              These codes were won but couldn&apos;t be created in Shopify (often the app is missing the
              <code> write_discounts</code>/<code>write_customers</code> scope — reinstalling re-grants it).
              Fix the cause, then retry — the codes become redeemable without the shopper losing their prize.
            </p>
          )}
        </Banner>
      ) : null}

      {coupons.data && coupons.data.length === 0 ? (
        <Card>
          <EmptyStatePanel
            heading="No coupons yet"
            primaryAction={{ content: 'View campaigns', onAction: () => navigate('/campaigns') }}
          >
            <p>When customers win a discount reward, the generated code appears here with its status and usage.</p>
          </EmptyStatePanel>
        </Card>
      ) : null}

      {coupons.data && coupons.data.length > 0 ? (
        <Card padding="0">
          <ScrollArea maxHeight={560}>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text']}
              headings={['Code', 'Discount', 'Status', 'Checkout', 'Used', 'Created']}
              rows={rows}
            />
          </ScrollArea>
        </Card>
      ) : null}
      {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </Page>
  );
}
